import React, { useState } from 'react'
import Datalist from 'components/Datalist'
import {
  GetPrice_getCompanyPrice,
  useAuth,
  useGetCompanyPrice,
  UserPermissions,
  useDeleteCompanyPrice,
  SubLocation,
  SublocationName,
} from '@modmd/data'
import Card from 'components/Card'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { ConfirmationDialog, Dialog } from 'components/Dialog'
import { useToaster } from 'utils/useToaster'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import Label from 'components/Label'
import { CreateEditPrices } from '../components/CreateEditPrices'

const SKELETON_DETAILS_COLUMNS = 4
const SKELETON_DETAILS_ROWS = 1

interface Props {
  companyId: string
  groupId?: string
}

const CompanyPriceDatalist = ({ companyId, groupId }: Props) => {
  const { setToastMessage } = useToaster()
  const [isEditingDialog, setIsEditingDialog] = useState(false)
  const [isAddingDialog, setIsAddingDialog] = useState(false)
  const [confirmDeletePrice, setConfirmDeletePrice] = useState(false)
  const [companyPriceItem, setCompanyPriceItem] = useState<GetPrice_getCompanyPrice | undefined>(
    undefined
  )
  const { hasPermission } = useAuth()
  const { data: companyPrices, loading, refetch } = useGetCompanyPrice({
    companyId,
    ...(groupId && { groupId }),
  })

  const onDismiss = () => {
    setCompanyPriceItem(undefined)
    setIsAddingDialog(false)
    setIsEditingDialog(false)
    setConfirmDeletePrice(false)
  }

  const onEditClick = (item: GetPrice_getCompanyPrice) => {
    setCompanyPriceItem(item)
    setIsEditingDialog(true)
  }

  const onSubmit = () => {
    void refetch()
    onDismiss()
  }

  const [DeletePrice, { loading: isDeleteLoading }] = useDeleteCompanyPrice({
    onCompleted: () => {
      setToastMessage('Data successfully deleted', 'success')
      onSubmit()
    },
  })

  const handleDelete = (
    item: GetPrice_getCompanyPrice,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setConfirmDeletePrice(true)
    setCompanyPriceItem(item)
  }

  const closeDeleteDialog = async () => {
    await refetch()
    onDismiss()
  }

  const onDelete = async () => {
    await DeletePrice({
      variables: {
        id: String(companyPriceItem?.id),
      },
    })
    await closeDeleteDialog()
  }

  return (
    <>
      <Datalist
        isLoading={loading}
        skeletonProps={{
          columns: SKELETON_DETAILS_COLUMNS,
          rows: SKELETON_DETAILS_ROWS,
        }}
        data={companyPrices?.getCompanyPrice || []}
        shouldRenderTableHeader
        showActionIndicators
        header={
          <Datalist.Title
            rightContent={
              hasPermission([UserPermissions.ADD_COMPANY_GROUP_PRICE]) && (
                <>
                  <Button
                    size="small"
                    marginLeft="auto"
                    colorVariant="primary"
                    onClick={() => setIsAddingDialog(true)}
                  >
                    <Icon.Plus size="L" /> Add Price
                  </Button>
                </>
              )
            }
          >
            Prices
          </Datalist.Title>
        }
        renderTableHeader={() => (
          <Datalist.Row gridTemplateColumns="2fr 0.5fr 1fr 1fr 0.5fr">
            <Datalist.HeaderCell label="Name" />
            <Datalist.HeaderCell label="Price" />
            <Datalist.HeaderCell label="Min. Quantity" />
            <Datalist.HeaderCell label="Rate type" />
            {hasPermission([UserPermissions.DELETE_GROUP, UserPermissions.DELETE_COMPANY]) ? (
              <Datalist.HeaderCell label="Actions" />
            ) : null}
          </Datalist.Row>
        )}
        renderTableRow={(item) => (
          <Datalist.Row
            display="grid"
            gridTemplateColumns="2fr 0.5fr 1fr 1fr 0.5fr"
            onClick={() => onEditClick(item)}
          >
            <Datalist.Cell>
              <Box display="flex" flexDirection="column">
                {item.Product?.name} - {item.Product?.type}
                {item?.Group?.name && !groupId && (
                  <>
                    <Label size="l" indicator="grayDark">
                      Group Name: {item?.Group?.name}
                    </Label>
                  </>
                )}
              </Box>
            </Datalist.Cell>
            <Datalist.Cell>{item.price}</Datalist.Cell>
            <Datalist.Cell>{item.minQuantity}</Datalist.Cell>
            <Datalist.Cell>{SublocationName[item.sublocation as SubLocation]}</Datalist.Cell>
            {hasPermission([UserPermissions.DELETE_GROUP, UserPermissions.DELETE_COMPANY]) ? (
              <Datalist.Cell>
                <Button
                  disabled={isDeleteLoading}
                  appearance="ghost"
                  colorVariant="danger"
                  size="small"
                  onClick={(e) => {
                    handleDelete(item, e)
                  }}
                >
                  <Icon.Trash />
                </Button>
              </Datalist.Cell>
            ) : null}
          </Datalist.Row>
        )}
      />
      <Dialog isOpen={!!isEditingDialog} onDismiss={onDismiss} variant="center" maxWidth="545px">
        <Card.Title onDismiss={onDismiss}>Edit Prices</Card.Title>
        <Card.Content>
          <>
            {companyPrices && companyPrices.getCompanyPrice.length > 0 && (
              <CreateEditPrices
                companyId={companyId}
                groupId={groupId}
                companyPriceItem={companyPriceItem}
                isAdding={false}
                onSubmit={onSubmit}
              />
            )}
          </>
        </Card.Content>
      </Dialog>
      <Dialog isOpen={!!isAddingDialog} onDismiss={onDismiss} variant="center" maxWidth="545px">
        <Card.Title onDismiss={onDismiss}>Add Prices</Card.Title>
        <Card.Content>
          <CreateEditPrices
            companyId={companyId}
            groupId={groupId}
            companyPriceItem={companyPriceItem}
            isAdding
            onSubmit={onSubmit}
          />
        </Card.Content>
      </Dialog>
      <ConfirmationDialog
        title="Deleting Member from Session"
        isOpen={confirmDeletePrice}
        onDismiss={() => setConfirmDeletePrice(false)}
        actions={
          <>
            <Button onClick={() => setConfirmDeletePrice(false)} colorVariant="secondary">
              Cancel
            </Button>
            <Button onClick={onDelete} isFetching={isDeleteLoading} disabled={isDeleteLoading}>
              Delete
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column">
          <Text>
            Are you sure you want to remove {companyPriceItem?.Product?.name} with the price of $
            {companyPriceItem?.price}?
          </Text>
        </Box>
      </ConfirmationDialog>
    </>
  )
}

export { CompanyPriceDatalist }
